import { type ApolloError, gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BANK_CONNECTIONS_QUERY } from "../bank-connections/get-all";

type DeleteBankAccountInput = {
  bankAccountId: string;
};

type DeleteBankAccountResponse = {
  deleteBankAccount: boolean;
};

export const useDeleteBankAccount = (): [
  (
    data: DeleteBankAccountInput,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<boolean>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();

  const [mutate, { loading, error }] = useMutation<DeleteBankAccountResponse>(
    DELETE_BANK_ACCOUNT_MUTATION,
    {
      refetchQueries: [BANK_CONNECTIONS_QUERY],
      awaitRefetchQueries: true,
    },
  );

  const handleDelete = async (
    data: DeleteBankAccountInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));
    try {
      const res = await mutate({ variables: data });
      if (!res.data) {
        throw new Error("No data");
      }
      toast.success(t("bank_connections.toasts.account_deleted"), {
        id: toastId,
      });
      onSuccess();
      return res.data.deleteBankAccount;
    } catch (e) {
      toast.error(t("bank_connections.toasts.delete_account_error"), {
        id: toastId,
        richColors: true,
      });
      onError();
      throw e;
    }
  };

  return [handleDelete, { loading, error }];
};

export const DELETE_BANK_ACCOUNT_MUTATION = gql`
  mutation DeleteBankAccount($bankAccountId: String!) {
    deleteBankAccount(bankAccountId: $bankAccountId)
  }
`;
